// =============================================================================
// client/src/components/OutreachTab.jsx | v1.0 | S80 | P9b — W-10
// =============================================================================
// The recruit's Outreach tab: connect card → Assigned RC → viewers → campaigns
// → digest. Self-contained, in PaymentsTab's shape, so RecruitEdit gains one
// line rather than a section.
//
// 🔴 THE CONNECT MACHINE IS P9a's AND IS CONSUMED UNCHANGED. Eleven states,
// ruled at P9a §0.6 and shipped in client/src/lib/flux/connect.js. This file
// DEFINES NO STATE. `sending` is a LOCAL UI FLAG here, not a twelfth state —
// it says a request is in flight, not what the mailbox is.
//
// `verified` is the only state that enables real mail. `connected` proves a
// token ROW exists; `verified` proves the token WORKS. Collapsing them is how a
// client ships a Launch button over a dead token, which is why /gmail-verify is
// called ONCE PER SUCCESSFUL connect episode (ruling 16: poll cheap, verify
// once — BENT at S99 on Jake's ruling, same bend as ConnectGmail/adddbb8: a
// verify that FAILS mid-poll re-arms, because a dead token row is what every
// re-auth episode starts with. Never-verify-on-keystrokes is untouched).
// =============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { can } from '../lib/can.js';
import {
  CONNECT_STATES, offersConnectControls, stateForStatusResult, stateForVerifyResult,
  pollDelayMs, pollDeadlineMs,
} from '../lib/flux/connect.js';
import { severityClass } from '../lib/flux/status.js';
import Toast from './Toast.jsx';
import ConfirmModal from './ConfirmModal.jsx';

const S = CONNECT_STATES;

const STATE_COPY = {
  [S.IDLE]: 'No recruit selected.',
  [S.CHECKING]: 'Checking Gmail…',
  [S.NOT_CONNECTED]: 'Not connected',
  [S.POLLING]: 'Waiting for authorization…',
  [S.EXPIRED]: 'Not yet authorized',
  [S.CONNECTED]: 'Connected — verifying…',
  [S.VERIFIED]: '✓ Connected',
  [S.REVOKED]: '⚠ Needs re-authorization',
  [S.BLOCKED]: 'Needs a human',
  [S.AMBIGUOUS]: 'More than one Gmail connection is on file for this recruit — an admin has to resolve it.',
  [S.ERROR]: 'Could not check the connection',
};

export default function OutreachTab({ recruitId, role }) {
  const [toast, setToast] = useState(null);
  return (
    <div className="space-y-6">
      <ConnectCard recruitId={recruitId} role={role} onToast={setToast} />
      <AssignedRc recruitId={recruitId} role={role} onToast={setToast} />
      <ViewersAllowlist recruitId={recruitId} role={role} onToast={setToast} />
      <RecruitCampaigns recruitId={recruitId} />
      <DigestSurfaces recruitId={recruitId} onToast={setToast} />
      <Toast message={toast?.message} tone={toast?.tone} onDismiss={() => setToast(null)} />
    </div>
  );
}

// ─── 1. Connect status ───────────────────────────────────────────────────────

function ConnectCard({ recruitId, role, onToast }) {
  const [state, setState] = useState(S.CHECKING);
  const [mailbox, setMailbox] = useState('');
  const [reason, setReason] = useState(null);
  const [sending, setSending] = useState(false);          // LOCAL UI FLAG
  const [linkExpiry, setLinkExpiry] = useState(null);
  const [inviteExpiry, setInviteExpiry] = useState(null);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [campaignCount, setCampaignCount] = useState(null);
  const pollTimer = useRef(null);
  const pollStarted = useRef(null);
  // 🔴 S98. The instant this episode stops at, from the SERVER's expires_at —
  // 24 h for an emailed invite, ~15 min for a copied link. Not a constant.
  const pollDeadline = useRef(0);
  const verifiedFor = useRef(null);   // one SUCCESSFUL verify per connect episode (S99)

  const stopPolling = useCallback(() => {
    // ⚠ clearTimeout, not clearInterval: the loop self-reschedules because the
    // cadence changes mid-episode, and setInterval's period is fixed at birth.
    if (pollTimer.current) { clearTimeout(pollTimer.current); pollTimer.current = null; }
  }, []);

  const verifyOnce = useCallback(async (address, { fromPoll = false } = {}) => {
    // Ruling 16 (bent at S99 — see the header): one SUCCESSFUL Google
    // round-trip per episode. The ref is what makes "once" true across
    // re-renders and a second poll tick; a failed mid-poll verify clears it
    // below so the next tick asks again. Returns the computed state so the
    // polling caller can decide whether the episode is over.
    if (!address || verifiedFor.current === address) return undefined;
    verifiedFor.current = address;
    let next;
    let why;
    try {
      const body = await api.get(`/api/flux/campaigns/gmail-verify?gmail=${encodeURIComponent(address)}`);
      next = stateForVerifyResult(body);
      why = body?.reason || null;
    } catch (err) {
      next = S.ERROR;
      why = err.body?.error || err.message;
    }
    // 🔴 S99, ported from ConnectGmail (adddbb8). A FAILED verify during a poll
    // is "not yet", not an answer — every re-auth episode begins with a token
    // row that exists and is dead, and a transient ERROR (connect.js maps
    // `timeout` there on purpose) is not an answer either. No state write: the
    // card stays POLLING, and clearing verifiedFor is what lets the next tick
    // ask Google again instead of hitting the once-per-episode guard above.
    if (fromPoll && (next === S.REVOKED || next === S.ERROR)) {
      verifiedFor.current = null;
      return next;
    }
    setState(next);
    setReason(why);
    return next;
  }, []);

  const checkStatus = useCallback(async ({ verify = true, fromPoll = false } = {}) => {
    try {
      const body = await api.get(`/api/flux/oauth/status?recruitId=${recruitId}`);
      const next = stateForStatusResult(body);
      if (next === S.CONNECTED) {
        setMailbox(body.gmail_address || '');
        if (!fromPoll) {
          setState(S.CONNECTED);
          stopPolling();
          if (verify) await verifyOnce(body.gmail_address);
          return next;
        }
        // 🔴 S99, ported from ConnectGmail (adddbb8). `connected: true` is NOT
        // the poll's success predicate — by connect.js's own header it proves a
        // token ROW exists, nothing about the token WORKING. A re-authorization
        // STARTS with a row that exists and is dead, so stopping here killed
        // every re-auth episode on its first tick. During a poll, only a
        // WORKING token — or a verdict re-auth cannot fix — ends the episode;
        // the tick stops on the VERDICT returned here. Do not "simplify" the
        // stopPolling() back in.
        return await verifyOnce(body.gmail_address, { fromPoll: true });
      }
      // ⚠ AMBIGUOUS RENDERS NO ADDRESS AND NO INVENTED COUNT: /status returns
      // neither on that branch. The mailboxes appear only in the disconnect
      // 409 body, after an admin asks for them. It is an answer a poll cannot
      // fix, so it writes through even mid-poll and the tick stops on it.
      if (next === S.AMBIGUOUS) { setMailbox(''); setState(next); return next; }
      // not_connected while polling is the normal waiting case: stay polling,
      // write nothing (S99 — the pre-port tick wrote NOT_CONNECTED over
      // POLLING and wiped the mailbox input on its first waiting tick).
      if (fromPoll) return next;
      setMailbox('');
      setState(next);
      return next;
    } catch (err) {
      if (err.status === 403) { setState(S.BLOCKED); setReason('Not visible to your account'); return S.BLOCKED; }
      if (fromPoll) return S.ERROR;   // one failed poll is not a state change
      setState(S.ERROR);
      setReason(err.body?.error || err.message);
      return S.ERROR;
    }
  }, [recruitId, stopPolling, verifyOnce]);

  useEffect(() => {
    verifiedFor.current = null;
    checkStatus();
    return stopPolling;
  }, [checkStatus, stopPolling]);

  // The confirmation names how many live campaigns this destroys the sender
  // for. A count the operator has to go and find is a count they will not find.
  useEffect(() => {
    api.get(`/api/flux/campaigns/recruit/${recruitId}/portal?limit=100`)
      .then((b) => setCampaignCount(
        (b?.campaigns || []).filter((c) => c.status === 'sending' || c.status === 'queued').length,
      ))
      .catch(() => setCampaignCount(null));
  }, [recruitId, state]);

  async function disconnect() {
    setDisconnectOpen(false);
    setSending(true);
    try {
      const body = await api.post('/api/flux/oauth/disconnect', {
        recruit_id: recruitId, gmail_address: mailbox || undefined,
      });
      const paused = (body.campaigns_paused || []).length;
      onToast({
        message: `${body.gmail_address} disconnected.`
          + (paused ? ` ${paused} campaign(s) paused — they resume if this mailbox is reconnected.` : '')
          + (body.google_revoked ? '' : ' Google did not confirm the revoke; the CCF-side binding is gone either way.'),
        tone: 'success',
      });
      verifiedFor.current = null;
      setMailbox('');
      await checkStatus({ verify: false });
    } catch (err) {
      // A 409 lists the mailboxes when there is more than one on file — the
      // only place in the product that list is ever rendered.
      const list = err.body?.mailboxes;
      onToast({
        message: (err.body?.error || err.message) + (list ? ` (${list.join(', ')})` : ''),
        tone: 'error',
      });
    } finally {
      setSending(false);
    }
  }

  // 🔴 S98. FAST THEN SLOW, STOPPING AT THE SERVER'S OWN DEADLINE.
  // Every 5 s for the first 10 minutes, then every 5 minutes until `expiresAt`.
  // ⚠ THE SAME DEFECT LIVED HERE AND IN ConnectGmail AND BOTH GOT THE SAME FIX:
  // a hardcoded 10-minute stop, while `Send connect invite` mints a link good
  // for 24 hours. The card went EXPIRED with ~23h45m of life left on the link.
  // ⚠ The deadline is NOT a constant — `Copy connect link` mints ~15 minutes and
  // `Send connect invite` mints 24 hours, and both stop correctly because the
  // caller passes the expiry the SERVER returned. See lib/flux/connect.js.
  function startPolling(expiresAt) {
    stopPolling();
    const startedAt = Date.now();
    pollStarted.current = startedAt;
    pollDeadline.current = pollDeadlineMs(expiresAt, startedAt);
    setState(S.POLLING);
    const tick = async () => {
      const now = Date.now();
      if (now >= pollDeadline.current) {
        stopPolling();
        setState(S.EXPIRED);
        return;
      }
      // 🔴 S99: stop on the VERDICT, not on a row existing. VERIFIED and
      // BLOCKED — and AMBIGUOUS, an admin problem no poll fixes — end the
      // episode; a dead token or a transient error is "not yet".
      const verdict = await checkStatus({ fromPoll: true });
      if (verdict === S.VERIFIED || verdict === S.BLOCKED || verdict === S.AMBIGUOUS) { stopPolling(); return; }
      // Re-read the timer ref: stopPolling may have fired while checkStatus was
      // in flight (Cancel, unmount, a recruit change), and rescheduling then
      // would resurrect a loop the component believes it already ended.
      if (pollTimer.current === null) return;
      pollTimer.current = setTimeout(tick, pollDelayMs(now - startedAt));
    };
    pollTimer.current = setTimeout(tick, pollDelayMs(0));
  }

  async function sendInvite() {
    setSending(true);
    try {
      // S99, ported from ConnectGmail: a fresh invite is a fresh episode.
      // Without this, verifiedFor still holds the address from the mount-time
      // verify and the once-per-episode guard blocks every re-auth verify —
      // the poll would run to its deadline blind and land EXPIRED.
      verifiedFor.current = null;
      const body = await api.post('/api/flux/oauth/send-invite', {
        recruit_id: recruitId, gmail_address: mailbox,
      });
      setInviteExpiry(body.expires_at);
      onToast({ message: `Invite sent to ${body.sent_to}. Waiting for them to connect…`, tone: 'success' });
      startPolling(body.expires_at);
    } catch (err) {
      // A 429 carries the wait in its own message; a 409 names the bound
      // mailbox. Both are rendered as the server wrote them.
      onToast({ message: err.body?.error || err.message, tone: 'error' });
    } finally {
      setSending(false);
    }
  }

  async function copyLink() {
    setSending(true);
    try {
      verifiedFor.current = null;   // a fresh link is a fresh episode (S99)
      const body = await api.post('/api/flux/oauth/google/url', {
        recruit_id: recruitId, gmail_address: mailbox,
      });
      await navigator.clipboard?.writeText(body.url);
      setLinkExpiry(body.expires_at);
      onToast({ message: 'Connect link copied. It is short-lived — send it now.', tone: 'success' });
      startPolling(body.expires_at);
    } catch (err) {
      onToast({ message: err.body?.error || err.message, tone: 'error' });
    } finally {
      setSending(false);
    }
  }

  const controls = offersConnectControls(state);
  // S90 P12A item 1 — THE GUARD IS UNCHANGED, only its render site moved.
  // Disconnect destroys a live credential a teenager has to re-grant, so it
  // stays admin/manager (`oauth.disconnect`), stays typed-confirmed, and its
  // confirmation still names both the mailbox and how many live campaigns lose
  // their sender. Hoisted to a const because the panel above now needs to know
  // whether the control will render before it decides to draw the row at all.
  const canDisconnect = can(role, 'oauth.disconnect')
    && (state === S.VERIFIED || state === S.CONNECTED || state === S.REVOKED || state === S.AMBIGUOUS);

  return (
    <section className="card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-black text-hunter text-lg">Gmail connection</h3>
        <span className={`px-2 py-0.5 rounded text-[0.65rem] font-bold ${
          state === S.VERIFIED ? severityClass('green')
            : state === S.BLOCKED || state === S.REVOKED ? severityClass('red')
              : state === S.AMBIGUOUS ? severityClass('orange') : severityClass('gray')}`}>
          {state.replace('_', ' ').toUpperCase()}
        </span>
      </div>

      <p className="text-sm text-silver-text">{STATE_COPY[state]}</p>
      {/* S90 P12A item 1 — THE MAILBOX AND THE CONTROL THAT DESTROYS IT NOW SHARE
          A PANEL. Disconnect used to sit in its own bordered block at the foot
          of the card, detached from the address it acts on, so the operator read
          the address in one place and pressed the button in another. Same guard,
          same typed confirmation, same irreversibility — placement only. */}
      {(state === S.VERIFIED && mailbox) || canDisconnect ? (
        <div className="mt-1 flex items-center justify-between gap-3">
          <p className="text-sm text-silver-mute">{state === S.VERIFIED ? mailbox : ''}</p>
          {canDisconnect && (
            <button type="button" className="text-xs font-bold text-red-700 shrink-0" disabled={sending}
              onClick={() => setDisconnectOpen(true)}>
              Disconnect this mailbox
            </button>
          )}
        </div>
      ) : null}
      {/* BLOCKED renders the server's reason and NO ACTION BUTTON: a re-auth
          email fixes neither an identity divergence nor a server
          misconfiguration, and sending one wastes the kid's time. */}
      {(state === S.BLOCKED || state === S.ERROR || state === S.REVOKED) && reason && (
        <p className="text-xs text-silver-mute mt-1">{reason}</p>
      )}

      {controls && (
        <div className="mt-4 space-y-3">
          <div>
            <div className="label-dark mb-1">Mailbox to connect</div>
            <input
              className="input" value={mailbox} placeholder="the address the link is bound to"
              onChange={(e) => { setMailbox(e.target.value); verifiedFor.current = null; }}
            />
            <p className="text-xs text-silver-mute mt-1">
              Often a parent&apos;s or a school address — edit it before sending.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {can(role, 'oauth.invite') && (
              <button type="button" className="btn-primary" disabled={sending || !mailbox} onClick={sendInvite}>
                {sending ? 'Sending…' : state === S.POLLING ? 'Resend invite' : 'Send connect invite'}
              </button>
            )}
            <button type="button" className="btn-secondary" disabled={sending || !mailbox} onClick={copyLink}>
              Copy connect link
            </button>
            {state === S.POLLING && (
              <button type="button" className="btn-secondary" onClick={() => { stopPolling(); checkStatus(); }}>
                Cancel
              </button>
            )}
            {(state === S.EXPIRED || state === S.ERROR) && (
              <button type="button" className="btn-secondary" onClick={() => checkStatus()}>Recheck</button>
            )}
          </div>
          {inviteExpiry && <p className="text-xs text-silver-mute">Invite link good until {inviteExpiry}.</p>}
          {linkExpiry && <p className="text-xs text-silver-mute">Copied link expires {linkExpiry}.</p>}
        </div>
      )}

      {state === S.CONNECTED && (
        <button type="button" className="btn-secondary mt-4" onClick={() => { verifiedFor.current = null; verifyOnce(mailbox); }}>
          Verify now
        </button>
      )}

      <ConfirmModal
        open={disconnectOpen}
        destructive
        requireTyped="DISCONNECT"
        title="Disconnect this mailbox"
        body={`Deletes the connection for ${mailbox || 'this recruit'} and asks Google to revoke it. `
          + `The recruit has to grant permission again to undo this — you cannot undo it for them.`
          + (campaignCount ? `

${campaignCount} live campaign(s) will pause. They resume if this same mailbox is reconnected to this recruit; otherwise archive them and rebuild on the new one.` : '')}
        confirmLabel="Disconnect"
        onCancel={() => setDisconnectOpen(false)}
        onConfirm={disconnect}
      />
    </section>
  );
}

// ─── 2. Assigned RC ──────────────────────────────────────────────────────────
// CLIENT-GATED ONLY, and that is deliberate: S-9 is dropped under ruling 36, so
// the server still accepts this column from any team member. The field is
// offered to admin/manager because that is who should be setting it — not
// because the server refuses anyone else. Do not read this as an enforcement.
function AssignedRc({ recruitId, role, onToast }) {
  const [members, setMembers] = useState([]);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const editable = can(role, 'recruit.assign_rc');

  useEffect(() => {
    api.get('/api/recruits/team-members').then((b) => setMembers(b?.team_members || [])).catch(() => {});
    api.get(`/api/recruits/${recruitId}`).then((r) => setValue(r?.assigned_rc_id || '')).catch(() => {});
  }, [recruitId]);

  async function save(next) {
    setValue(next);
    setSaving(true);
    try {
      await api.patch(`/api/recruits/${recruitId}`, { assigned_rc_id: next || null });
      onToast({ message: 'Assigned coordinator updated.', tone: 'success' });
    } catch (err) {
      onToast({ message: err.body?.error || err.message, tone: 'error' });
    } finally {
      setSaving(false);
    }
  }

  const current = members.find((m) => m.id === value);

  return (
    <section className="card">
      <h3 className="font-display font-black text-hunter text-lg mb-2">Assigned coordinator</h3>
      {editable ? (
        <select className="input" value={value} disabled={saving} onChange={(e) => save(e.target.value)}>
          <option value="">— unassigned —</option>
          {members.map((m) => <option key={m.id} value={m.id}>{m.full_name} ({m.role})</option>)}
        </select>
      ) : (
        <p className="text-sm text-silver-text">{current ? `${current.full_name} (${current.role})` : 'Unassigned'}</p>
      )}
      <p className="text-xs text-silver-mute mt-2">
        Hot-lead alerts for this recruit&apos;s campaigns go to whoever is assigned here.
      </p>
    </section>
  );
}

// ─── 3. Viewers allowlist (W-9) ──────────────────────────────────────────────
function ViewersAllowlist({ recruitId, role, onToast }) {
  const [viewers, setViewers] = useState([]);
  const [members, setMembers] = useState([]);
  const [adding, setAdding] = useState('');
  const manage = can(role, 'viewers.manage');

  const load = useCallback(() => {
    api.get(`/api/flux/recruits/${recruitId}/viewers`)
      .then((b) => setViewers(b?.viewers || []))
      .catch(() => setViewers([]));
  }, [recruitId]);

  useEffect(() => {
    load();
    api.get('/api/recruits/team-members').then((b) => setMembers(b?.team_members || [])).catch(() => {});
  }, [load]);

  async function grant() {
    if (!adding) return;
    try {
      const body = await api.post(`/api/flux/recruits/${recruitId}/viewers`, { team_id: adding });
      onToast({
        message: body.already ? 'That person already had access.' : 'Access granted.',
        tone: 'success',
      });
      setAdding('');
      load();
    } catch (err) {
      onToast({ message: err.body?.error || err.message, tone: 'error' });
    }
  }

  async function revoke(teamId) {
    try {
      await api.del(`/api/flux/recruits/${recruitId}/viewers/${teamId}`);
      onToast({ message: 'Access revoked.', tone: 'success' });
      load();
    } catch (err) {
      onToast({ message: err.body?.error || err.message, tone: 'error' });
    }
  }

  // Admin and manager already see every recruit, and an existing viewer cannot
  // be granted twice — filtering both out means the picker only ever offers a
  // choice the server will accept.
  const grantable = members.filter(
    (m) => m.role !== 'admin' && m.role !== 'manager' && !viewers.some((v) => v.team_id === m.id),
  );

  return (
    <section className="card">
      <h3 className="font-display font-black text-hunter text-lg mb-2">Who else can see this recruit&apos;s outreach</h3>

      {viewers.length === 0 ? (
        <p className="text-sm text-silver-mute">Only admins, managers and the assigned coordinator.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {viewers.map((v) => (
            <span key={v.team_id} className="px-3 py-1 rounded-full text-xs bg-gray-100 text-silver-text">
              {v.full_name || v.team_id} · {v.role}
              {v.created_at ? ` · since ${String(v.created_at).slice(0, 10)}` : ''}
              {manage && (
                <button type="button" className="ml-2 font-bold" aria-label="Revoke"
                  onClick={() => revoke(v.team_id)}>×</button>
              )}
            </span>
          ))}
        </div>
      )}

      {manage && grantable.length > 0 && (
        <div className="flex gap-2 mt-3">
          <select className="input" value={adding} onChange={(e) => setAdding(e.target.value)}>
            <option value="">Add viewer…</option>
            {grantable.map((m) => <option key={m.id} value={m.id}>{m.full_name} ({m.role})</option>)}
          </select>
          <button type="button" className="btn-secondary" disabled={!adding} onClick={grant}>Add</button>
        </div>
      )}
    </section>
  );
}

// ─── 4. Campaigns for this recruit ───────────────────────────────────────────
function RecruitCampaigns({ recruitId }) {
  const [campaigns, setCampaigns] = useState([]);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    api.get(`/api/flux/campaigns/recruit/${recruitId}/portal?limit=50`)
      .then((b) => setCampaigns(b?.campaigns || []))
      .catch((err) => { if (err.status === 403) setDenied(true); });
  }, [recruitId]);

  if (denied) {
    return (
      <section className="card">
        <h3 className="font-display font-black text-hunter text-lg mb-2">Campaigns</h3>
        <p className="text-sm text-silver-mute">Not visible to your account.</p>
      </section>
    );
  }

  return (
    <section className="card">
      <h3 className="font-display font-black text-hunter text-lg mb-2">Campaigns</h3>
      {campaigns.length === 0 ? (
        <p className="text-sm text-silver-mute">No campaigns for this recruit yet.</p>
      ) : (
        <ul className="space-y-2">
          {campaigns.map((c) => (
            <li key={c.id} className="flex items-center justify-between text-sm">
              <a className="text-mid-green font-bold" href={`/campaigns/${c.id}`}>{c.name}</a>
              <span className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-[0.65rem] font-bold ${severityClass(c.status_display?.severity)}`}>
                  {c.status_display?.badge || '—'}
                </span>
                <span className="text-silver-mute">{c.status_display?.label}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── 5. Digest surfaces ──────────────────────────────────────────────────────
// ONE control: the opt-out switch. It PATCHes /api/recruits/:id and never
// touches the digest itself, which is why it survives.
//
// 🔴 S87 — "PREVIEW DIGEST" AND "SEND ME A TEST" WERE REMOVED, ALONG WITH THEIR
// ROUTES. Neither was ever ruled. Jake's only input on this feature is
// Enhancements C-19 — "It should come from Skip, and that channel should be
// available via resend" — which authorizes the family email and nothing else.
// The preview was C-19's own stepping stone, justified as letting Jake judge
// the product before it was a product; he answered by authorizing the product,
// and its purpose ended there. The test send is in no ruling at all, and its
// zero-week suppression was stripped so this button would not feel broken —
// which is how a digest full of invented claims reached a real inbox.
//
// ⚠ DO NOT REBUILD EITHER ONE HERE. There is no route behind them any more, and
// a control that calls nothing is worse than no control. The digest has exactly
// one way to reach anyone: Friday's real send.
function DigestSurfaces({ recruitId, onToast }) {
  const [optedOut, setOptedOut] = useState(null);

  useEffect(() => {
    api.get(`/api/recruits/${recruitId}`)
      .then((r) => setOptedOut(!!r?.digest_opt_out_at))
      .catch(() => {});
  }, [recruitId]);

  async function toggle(next) {
    setOptedOut(next);
    try {
      await api.patch(`/api/recruits/${recruitId}`, {
        digest_opt_out_at: next ? new Date().toISOString() : null,
      });
      onToast({ message: next ? 'Family digest turned off.' : 'Family digest turned on.', tone: 'success' });
    } catch (err) {
      setOptedOut(!next);
      onToast({ message: err.body?.error || err.message, tone: 'error' });
    }
  }

  return (
    <section className="card">
      <h3 className="font-display font-black text-hunter text-lg mb-2">Family digest</h3>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={optedOut === false} disabled={optedOut === null}
          onChange={(e) => toggle(!e.target.checked)} />
        <span>Send this family the weekly digest</span>
      </label>
      <p className="text-xs text-silver-mute mt-2">
        The digest goes out Friday evenings, and only in a week when coaches actually engaged.
      </p>
    </section>
  );
}
